/**
 * Centralised VAT (TVA) profiles for Univers Cosmetic SaaS.
 *
 * SINGLE source of truth for VAT rates + labels across:
 *  - Customer form (Tax section)
 *  - Invoice creation (per-invoice VAT selector)
 *  - Portal ordering flows
 *  - Reporting / analytics filters
 *
 * Adding a new profile = add one entry to VAT_PROFILES. No code
 * changes required in consuming modules.
 *
 * Naming convention:
 *   key   – stable machine identifier (SCREAMING_SNAKE_CASE)
 *   rate  – percent as a number (20, 5.5, 0)  — NOT a fraction
 *   label – localised display string (EN)
 *   labelFr – localised display string (FR)
 *   description – short caption shown as helper text on forms
 *   customerFacing – when false, only invoice creators see the option
 *                    (currently every profile is customer-facing)
 */

export const VAT_PROFILE_KEYS = {
    FR_STANDARD: 'FR_STANDARD',
    FR_REDUCED: 'FR_REDUCED',
    EU_REVERSE_CHARGE: 'EU_REVERSE_CHARGE',
    EXPORT: 'EXPORT',
    CUSTOM: 'CUSTOM',
};

export const VAT_PROFILES = [
    {
        key: VAT_PROFILE_KEYS.FR_STANDARD,
        rate: 20,
        label: 'France Standard VAT',
        labelFr: 'TVA France Standard',
        description: 'Standard 20% rate for domestic B2B and B2C sales in France.',
    },
    {
        key: VAT_PROFILE_KEYS.FR_REDUCED,
        rate: 5.5,
        label: 'Reduced VAT',
        labelFr: 'TVA Réduite',
        description: 'Reduced 5.5% rate — applies to specific product categories '
            + '(alimentary, books, some cosmetics) per French tax code.',
    },
    {
        key: VAT_PROFILE_KEYS.EU_REVERSE_CHARGE,
        rate: 0,
        label: 'EU Reverse Charge',
        labelFr: 'Autoliquidation UE',
        description: '0% — customer accounts for VAT in their own EU country. '
            + 'Buyer VAT number must be present on the invoice.',
    },
    {
        key: VAT_PROFILE_KEYS.EXPORT,
        rate: 0,
        label: 'Export / Outside EU',
        labelFr: 'Export / Hors UE',
        description: '0% — sale outside the EU. Requires proof of export '
            + '(shipping documents, customs declaration).',
    },
    {
        key: VAT_PROFILE_KEYS.CUSTOM,
        rate: null, // user-supplied
        label: 'Custom',
        labelFr: 'Personnalisé',
        description: 'Enter a specific VAT rate for edge cases.',
    },
];

/** O(1) map access by key. */
const BY_KEY = Object.fromEntries(VAT_PROFILES.map((p) => [p.key, p]));

export const getVatProfile = (key) => BY_KEY[key] || null;

/**
 * Resolve the effective VAT rate for a given profile key + optional
 * custom-rate override. Falls back to 20% (`FR_STANDARD`) when nothing
 * matches so downstream math always has a number.
 */
export const resolveVatRate = (key, customRate) => {
    if (key === VAT_PROFILE_KEYS.CUSTOM) {
        const n = Number(customRate);
        return Number.isFinite(n) ? n : 20;
    }
    const p = BY_KEY[key];
    if (p && p.rate != null) return p.rate;
    return 20;
};

/**
 * Return a localised label for a profile key using the current i18n
 * language. Falls back to English.
 */
export const vatProfileLabel = (key, lang = 'en') => {
    const p = BY_KEY[key];
    if (!p) return key || '';
    return (lang || '').startsWith('fr') && p.labelFr ? p.labelFr : p.label;
};

/** Default profile applied to new customers who don't specify one. */
export const DEFAULT_VAT_PROFILE_KEY = VAT_PROFILE_KEYS.FR_STANDARD;
export const DEFAULT_VAT_RATE = 20;
